/* ---
	Product Hero

	Scroll-snap gallery for the product first view. The pill at the bottom
	left drives the track, the cue at the bottom right scrolls the page to
	the section that follows the hero.

	object-fit is handled in CSS: contain by default, optional cover on SP
	via the section setting (class kt-product-hero--mobile-cover).
--- */

if (typeof ProductHero !== 'function') {
  class ProductHero extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-product-hero-track]');
      if (!this.track) return;

      this.slides = [...this.track.querySelectorAll('[data-product-hero-slide]')];
      this.dots = [...this.querySelectorAll('[data-product-hero-dot]')];
      this.prev = this.querySelector('[data-product-hero-prev]');
      this.next = this.querySelector('[data-product-hero-next]');
      this.scrollCue = this.querySelector('[data-product-hero-scroll]');
      this.index = 0;

      if (this.slides.length > 1) {
        if (this.prev) this.prev.addEventListener('click', () => this.#goTo(this.index - 1));
        if (this.next) this.next.addEventListener('click', () => this.#goTo(this.index + 1));

        this.dots.forEach((dot, i) => {
          dot.addEventListener('click', () => this.#goTo(i));
        });

        this.SCROLL_EVENT = () => {
          if (this.rafId) return;
          this.rafId = requestAnimationFrame(() => {
            this.rafId = null;
            this.#syncFromScroll();
          });
        };
        this.track.addEventListener('scroll', this.SCROLL_EVENT, { passive: true });
      }

      if (this.scrollCue) {
        this.scrollCue.addEventListener('click', this.#onScrollCue.bind(this));
      }

      this.#update();
    }

    disconnectedCallback() {
      if (this.track && this.SCROLL_EVENT) {
        this.track.removeEventListener('scroll', this.SCROLL_EVENT);
      }
    }

    #goTo(index) {
      const total = this.slides.length;
      if (!total) return;
      /* Wraps, so the arrows are never dead ends on a short gallery. */
      const target = (index + total) % total;
      this.track.scrollTo({
        left: this.slides[target].offsetLeft,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
      this.index = target;
      this.#update();
    }

    #syncFromScroll() {
      const center = this.track.scrollLeft + this.track.clientWidth / 2;
      let closest = 0;
      let distance = Infinity;

      this.slides.forEach((slide, i) => {
        const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
        const delta = Math.abs(slideCenter - center);
        if (delta < distance) {
          distance = delta;
          closest = i;
        }
      });

      if (closest !== this.index) {
        this.index = closest;
        this.#update();
      }
    }

    #update() {
      this.dots.forEach((dot, i) => {
        dot.setAttribute('aria-current', i === this.index ? 'true' : 'false');
      });
      this.slides.forEach((slide, i) => {
        slide.setAttribute('aria-hidden', i === this.index ? 'false' : 'true');
      });
    }

    #onScrollCue(event) {
      /* Prefer the section right after the hero, so the cue keeps working
			   even when no anchor was set in the theme editor. */
      const target =
        (this.dataset.scrollTarget && document.querySelector(this.dataset.scrollTarget)) ||
        (this.closest('.shopify-section') || this).nextElementSibling;

      if (!target) return;

      event.preventDefault();
      target.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start',
      });
    }
  }

  customElements.define('product-hero', ProductHero);
}
